/**
 * CJAS — prayer time resolution.  Jira CA-20.
 *
 * This is the riskiest logic in the product: the countdown is the largest
 * element on the home screen and the first thing anyone notices when it is
 * wrong. It is written as a plain module with no dependencies so it can be
 * used directly by the TV display and the app, and ported line-for-line if
 * the backend lands on PHP.
 *
 * THE RULE THAT DRIVES EVERYTHING HERE:
 *   Prayer times are WALL-CLOCK values from a fixed annual timetable.
 *   They are never converted between timezones. 17:30 on the timetable is
 *   17:30 on the wall, on both sides of a daylight-saving change. Storing
 *   them as instants and converting is the bug this module exists to avoid.
 */

const PRAYERS = ['fajr', 'dhuhr', 'asr', 'maghrib', 'isha'];

/** "17:30" -> 1050 minutes. Returns null for empty/absent. */
function toMinutes(hhmm) {
  if (!hhmm) return null;
  const m = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(String(hhmm).trim());
  if (!m) throw new Error(`Invalid time: ${hhmm}`);
  return Number(m[1]) * 60 + Number(m[2]);
}

/** 1050 -> "17:30" */
function toHHMM(minutes) {
  const h = Math.floor(minutes / 60), m = minutes % 60;
  return String(h).padStart(2, '0') + ':' + String(m).padStart(2, '0');
}

/**
 * Merge the imported timetable row with any per-day override.
 * An override only ever replaces jamaat times and the closed flag — the
 * astronomical "begins" times come from the timetable and are not editable,
 * because they are facts about the sun, not decisions of the mosque.
 */
function resolveDay(timetableRow, overrideRow) {
  if (!timetableRow) return null;
  const o = overrideRow || {};

  const jamaat = {};
  for (const p of PRAYERS) {
    const key = `${p}_jamaat`;
    jamaat[p] = o[key] != null && o[key] !== '' ? o[key] : timetableRow[key];
  }
  const jumuaKhutbah = o.jumua_khutbah || timetableRow.jumua_khutbah || null;
  const jumuaJamaat  = o.jumua_jamaat  || timetableRow.jumua_jamaat  || null;

  return {
    date: timetableRow.date,
    begins: {
      fajr:    timetableRow.fajr_begins,
      sunrise: timetableRow.sunrise,
      dhuhr:   timetableRow.dhuhr_begins,
      asr:     timetableRow.asr_begins,
      maghrib: timetableRow.maghrib_begins,
      isha:    timetableRow.isha_begins,
    },
    jamaat: { ...jamaat, jumuaKhutbah, jumuaJamaat },
    isClosed: Boolean(o.is_closed),
    source: overrideRow ? 'override' : 'timetable',
    overrideReason: overrideRow ? (overrideRow.reason_no || null) : null,
    /*
     * FAULT 110. `overrides.resolve()` has returned the note since it was
     * written; this one — the resolver every surface actually goes through —
     * did not, so `overrideNote` was undefined before it ever reached a
     * payload. Two resolvers for one question, disagreeing about what an
     * override carries, is what let a validated, tested, read-back field be
     * null everywhere for five weeks.
     *
     * `overrides.test.js` asserts the two agree field for field now.
     */
    overrideNote: overrideRow ? (overrideRow.note_no || null) : null,
  };
}

/**
 * THE MOSQUE'S ZONE, not the phone's.  Audit F-07, 2026-10-19.
 *
 * A timetable time is a wall-clock time ON THE MOSQUE'S WALL. The server is pinned to the mosque's zone, so building it
 * in "local time" is right there — but the app builds the same instants on the phone, and a phone set to UTC (or
 * abroad) put an Oslo mosque's Isha jamaat 20:00 at 20:00 UTC: the countdown, the highlighted row and the reminders an
 * hour or more out. The app now says which zone the sheet is in (setZone, from GET /v1/mosque `timezone`); every
 * instant below is built in it. Unset (the server, the TV, a test) — or a zone this runtime's Intl cannot handle — it
 * is the runtime's own zone, exactly as before. Never set by the API: one process serves several mosques.
 */
let ZONE = null;
let ZONE_FMT = null;
function setZone(zone) {
  ZONE = null; ZONE_FMT = null;
  if (!zone || typeof zone !== 'string') return null;
  try {
    const fmt = new Intl.DateTimeFormat('en-US', { timeZone: zone, hourCycle: 'h23', year: 'numeric', month: '2-digit',
      day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' });
    const runtime = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const canonical = fmt.resolvedOptions().timeZone;
    fmt.formatToParts(new Date(0));                 // an engine without formatToParts keeps the runtime's zone
    if (canonical === runtime) return canonical;    // the same zone: the fast path below is already right
    ZONE = canonical; ZONE_FMT = fmt;
  } catch { ZONE = null; ZONE_FMT = null; }
  return ZONE;
}
/** The zone instants are built in: the one setZone() accepted, or null for the runtime's own. */
function zone() { return ZONE; }

/** An instant's wall-clock parts in the active zone (the runtime's when none is set). */
function wallParts(at) {
  const d = at instanceof Date ? at : new Date(at);
  if (!ZONE_FMT) {
    return { year: d.getFullYear(), month: d.getMonth() + 1, day: d.getDate(), hour: d.getHours(), minute: d.getMinutes(), second: d.getSeconds() };
  }
  const o = {};
  for (const p of ZONE_FMT.formatToParts(d)) o[p.type] = p.value;
  return { year: Number(o.year), month: Number(o.month), day: Number(o.day), hour: Number(o.hour) % 24, minute: Number(o.minute), second: Number(o.second) };
}
/** The calendar date "YYYY-MM-DD" of an instant on the mosque's wall (F-07: "today" is the mosque's today). */
function localDate(at = new Date()) {
  const p = wallParts(at), z = (n) => String(n).padStart(2, '0');
  return `${p.year}-${z(p.month)}-${z(p.day)}`;
}
/** "HH:MM" of an instant on the mosque's wall — for a notification that names the time it reminds of. */
function wallClock(at) {
  const p = wallParts(at);
  return `${String(p.hour).padStart(2, '0')}:${String(p.minute).padStart(2, '0')}`;
}
/** Friday, from a sheet's "YYYY-MM-DD" (calendar arithmetic — no zone can move it). */
function isFridayDate(dateStr) {
  return new Date(`${dateStr}T12:00:00Z`).getUTCDay() === 5;
}

/**
 * Build a local Date from a date string and a wall-clock time.
 * Uses the Date(y, m, d, h, min) constructor deliberately — it interprets
 * the parts in local time, which is exactly what a wall-clock timetable means.
 * Never use Date.parse on "YYYY-MM-DDTHH:MM" for this; that path has
 * historically been treated as UTC by some engines.
 * With a zone set (F-07) the same wall-clock time is found in THAT zone: start from the parts read as UTC and move by
 * the difference the zone shows, twice (once more across a clock change).
 */
function localDateTime(dateStr, hhmm, dayOffset = 0) {
  const [y, m, d] = dateStr.split('-').map(Number);
  const mins = toMinutes(hhmm);
  if (mins == null) return null;
  if (!ZONE_FMT) return new Date(y, m - 1, d + dayOffset, Math.floor(mins / 60), mins % 60, 0, 0);
  const want = Date.UTC(y, m - 1, d + dayOffset, Math.floor(mins / 60), mins % 60, 0, 0);
  let t = want;
  for (let i = 0; i < 3; i++) {
    const p = wallParts(new Date(t));
    const shown = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
    if (shown === want) break;
    t += want - shown;
  }
  return new Date(t);
}

/**
 * WHICH CALENDAR DAY EACH TIME ON A ROW ACTUALLY BELONGS TO.
 *
 * A timetable row is five wall-clock strings and nothing else — it carries no
 * date. Everywhere south of here that is enough, because the times rise from
 * fajr to isha and all five fall on the row's own date. **Oslo in June breaks
 * that.** True isha lands after midnight, so a row reading `isha 00:35` means
 * 00:35 TOMORROW, and building it on the row's own date puts it fourteen hours
 * in the past.
 *
 * The failure that caused is silent and it is the worst kind: isha is simply
 * skipped. The home screen jumps from maghrib to tomorrow's fajr, and the
 * countdown — the largest element on the screen and the first thing anyone
 * notices when it is wrong — is wrong for about six weeks a year. No error,
 * no empty state, just a missing prayer.
 *
 * So: walk the row in order, and every time a value goes BACKWARDS against the
 * one before it, the day has rolled over. Returns one offset per input, all
 * zero for an ordinary row. Mosques at this latitude usually fix a late isha
 * rather than chase a time that does not occur, so in practice this stays all
 * zeroes — but the platform must not depend on them doing that.
 */
function dayOffsets(values) {
  const out = [];
  let previous = -1, offset = 0;
  for (const v of values) {
    const mins = v == null || v === '' ? null : toMinutes(v);
    if (mins == null) { out.push(offset); continue; }
    if (mins < previous) offset += 1;
    previous = mins;
    out.push(offset);
  }
  return out;
}

/**
 * The next jamaat at a given moment.
 *
 * `days` is an ordered array of resolved days (today first). Tomorrow is
 * required so the answer after Isha is tomorrow's Fajr rather than nothing —
 * the gap between Isha and midnight is where a naive implementation shows
 * an empty home screen every single night.
 *
 * A closed day is skipped entirely.
 */
function nextJamaat(days, now = new Date()) {
  for (const day of days) {
    if (!day || day.isClosed) continue;

    // Friday: Jumu'ah replaces Dhuhr. From the sheet's date, not a Date in some zone (F-07).
    const isFriday = isFridayDate(day.date);

    // Build the actual sequence first — Jumu'ah substitutes for Dhuhr and can
    // sit at a different time, so the rollover check has to see what is really
    // being shown, not the unsubstituted row.
    // A prayer the mosque has set no jamaat for (a timetable of start times only) is counted to its
    // START, and marked `kind: 'begins'` so every surface says "begins", never "jamaat".
    // Friday's Dhuhr slot is NAMED Jumu'ah whether or not the mosque set a separate Jumu'ah time (audit F-08,
    // 2026-10-19; owner 2026-10-18 for the app): its time is then the Dhuhr jamaat, or Dhuhr's start. The app, the
    // TV, the API's `next` and the reminders now all say «jumua» on a Friday; before, only the app did.
    const sequence = PRAYERS.map((prayer) => {
      if (isFriday && prayer === 'dhuhr' && day.jamaat.jumuaJamaat) {
        return { label: 'jumua', time: day.jamaat.jumuaJamaat, kind: 'jamaat' };
      }
      const label = isFriday && prayer === 'dhuhr' ? 'jumua' : prayer;
      const j = day.jamaat[prayer];
      return j ? { label, time: j, kind: 'jamaat' }
        : { label, time: (day.begins && day.begins[prayer]) || null, kind: 'begins' };
    });
    const offsets = dayOffsets(sequence.map(x => x.time));

    for (let i = 0; i < sequence.length; i++) {
      const { label, time, kind } = sequence[i];
      if (!time) continue;

      const at = localDateTime(day.date, time, offsets[i]);
      if (at > now) {
        return {
          prayer: label,
          time,
          kind,
          at,
          secondsAway: Math.round((at - now) / 1000),
        };
      }
    }
  }
  return null; // caller must widen the window
}

/**
 * The next prayer to BEGIN, by the timetable's start times (owner, 2026-10-21: «jamaat time is not important here,
 * it's prayer start time and end time»). The home card counts to this when no prayer's window is open (inProgress);
 * while one is, it counts to that window's end. Friday's noon slot is named 'jumua' (as nextJamaat names it) and
 * begins when Dhuhr begins. A closed day has no prayer; a day without start times is skipped.
 */
function nextBegins(days, now = new Date()) {
  for (const day of days) {
    if (!day || day.isClosed || !day.begins) continue;
    const isFriday = isFridayDate(day.date);
    const times = PRAYERS.map((p) => day.begins[p] || null);
    const offsets = dayOffsets(times);
    for (let i = 0; i < PRAYERS.length; i++) {
      if (!times[i]) continue;
      const at = localDateTime(day.date, times[i], offsets[i]);
      if (at > now) {
        return { prayer: isFriday && PRAYERS[i] === 'dhuhr' ? 'jumua' : PRAYERS[i], time: times[i], kind: 'begins', at, secondsAway: Math.round((at - now) / 1000) };
      }
    }
  }
  return null;
}

/** Split seconds into the pieces the UI shows. */
function countdownParts(seconds) {
  const s = Math.max(0, seconds);
  return {
    hours: Math.floor(s / 3600),
    minutes: Math.floor((s % 3600) / 60),
    seconds: s % 60,
  };
}

/** Which prayer period we are currently inside — used to highlight a row. */
function currentPeriod(day, now = new Date()) {
  if (!day || day.isClosed) return null;
  // Same rollover rule as nextJamaat. Without it a past-midnight isha begins
  // time compares as "already started" from breakfast onwards, and the whole
  // day highlights the wrong row.
  const offsets = dayOffsets(PRAYERS.map(p => day.begins[p]));
  let current = null;
  for (let i = 0; i < PRAYERS.length; i++) {
    const begins = day.begins[PRAYERS[i]];
    if (!begins) continue;
    if (localDateTime(day.date, begins, offsets[i]) <= now) current = PRAYERS[i];
  }
  return current;
}

/**
 * When a prayer's time runs out, from the mosque's own timetable.  CA-44.
 *
 * A prayer may be prayed until the next one begins, and Fajr until sunrise —
 * so every end here is another published time on the same sheet, never a
 * calculation of our own. Isha ends at TOMORROW's fajr, which is on another
 * day: without `nextDay` there is no honest answer and this returns null
 * rather than guessing midnight.
 *
 * `null` means "we cannot say", and the caller shows nothing. A prayer app
 * that invents the minute a prayer stops being valid is worse than one that
 * stays quiet.
 */
function windowEnd(day, prayerKey, nextDay = null) {
  if (!day || !prayerKey) return null;
  if (prayerKey === 'jumua') prayerKey = 'dhuhr';
  if (prayerKey === 'fajr') return day.begins.sunrise || null;
  const i = PRAYERS.indexOf(prayerKey);
  if (i < 0) return null;
  if (prayerKey === 'isha') return (nextDay && nextDay.begins && nextDay.begins.fajr) || null;
  return day.begins[PRAYERS[i + 1]] || null;
}

/**
 * The prayer whose time is running NOW, and when it runs out.  (2026-09-27)
 *
 * At 21:06 with isha begun at 20:55 and no isha jamaat still to come, the
 * next thing is tomorrow's fajr — but the member is inside isha, and the
 * question they have is "how long do I have for isha?". This answers it.
 *
 * Inside a prayer means: its begins time has passed and its window
 * (windowEnd) has not ended. Fajr ends at sunrise, so between sunrise and
 * dhuhr nothing is running and this is null — unlike currentPeriod, which
 * keeps fajr highlighted until dhuhr. Isha needs `nextDay` for its end
 * (tomorrow's fajr); without it there is no honest end and this is null.
 * Every time here is from the sheet; nothing is calculated.
 */
/*
 * Two more cases (audit 2026-10-19), both through the 4th argument so every existing caller keeps its answer:
 *   prevDay  (F-06) between midnight and the day's Fajr, YESTERDAY's Isha is still running — until this Fajr. It
 *            asked only `day`, so at 00:00 «Bønnetid nå: Isha, slutter 05:16» turned into a plain countdown to Fajr.
 *            With `prevDay` it answers from yesterday's sheet, and says so (`date`).
 *   openEnd  (F-05) the last day the mosque has times for: Isha has begun and tomorrow's Fajr is not uploaded. It said
 *            nothing, and the home lost its card for the evening. With `openEnd` it answers Isha with no end
 *            (`ends`, `endsAt` and `secondsLeft` null, `endUnknown: true`) — never an invented midnight.
 * Every answer carries `date`, the sheet the prayer is on.
 */
function inProgress(day, nextDay = null, now = new Date(), { prevDay = null, openEnd = false } = {}) {
  const own = inProgressOn(day, nextDay, now, openEnd);
  if (own || !prevDay || !day || !day.begins || !day.begins.fajr) return own;
  // Only the sheet of the calendar day before (a gap in the cache is no "yesterday").
  if (!prevDay.date || localDateTime(prevDay.date, '12:00', 1).getTime() !== localDateTime(day.date, '12:00').getTime()) return own;
  // Only before this sheet's Fajr: from then on `day` answers for itself.
  const fajr = localDateTime(day.date, day.begins.fajr);
  if (!fajr || now >= fajr) return own;
  return inProgressOn(prevDay, day, now, false);
}

function inProgressOn(day, nextDay, now, openEnd) {
  if (!day || day.isClosed || !day.begins) return null;
  const keys = ['fajr', 'sunrise', 'dhuhr', 'asr', 'maghrib', 'isha'];
  const offsets = dayOffsets(keys.map((k) => day.begins[k]));
  const at = (k) => (day.begins[k] ? localDateTime(day.date, day.begins[k], offsets[keys.indexOf(k)]) : null);
  for (let i = PRAYERS.length - 1; i >= 0; i--) {
    const prayer = PRAYERS[i];
    const start = at(prayer);
    if (!start || start > now) continue;
    const end = windowEnd(day, prayer, nextDay);
    if (!end) {
      return prayer === 'isha' && openEnd && !nextDay
        ? { prayer, date: day.date, begins: day.begins[prayer], ends: null, endsAt: null, secondsLeft: null, endUnknown: true }
        : null;
    }
    const endsAt = prayer === 'isha'
      ? localDateTime(nextDay.date, end)
      : at(prayer === 'fajr' ? 'sunrise' : PRAYERS[i + 1]);
    if (!endsAt || endsAt <= now) return null;
    return {
      prayer,
      date: day.date,
      begins: day.begins[prayer],
      ends: end,
      endsAt,
      secondsLeft: Math.round((endsAt - now) / 1000),
    };
  }
  return null;
}

module.exports = {
  PRAYERS, toMinutes, toHHMM, resolveDay, localDateTime, dayOffsets,
  nextJamaat, nextBegins, countdownParts, currentPeriod, windowEnd, inProgress,
  setZone, zone, localDate, wallClock, isFridayDate,
};
